import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AuthImage from './AuthImage';
import BuildingSketchModal from './BuildingSketchModal';
import FloorPlanUploadButton from './FloorPlanUploadButton';
import { InfoButton } from './InfoModal';
import { WorkerMarkersInfoButton, DefaultBuildingInfoButton } from './BuildingWorkerInfo';
import { planUrlFromUploadResponse } from '../utils/fileUrl';

function resolveLevelPlanUrl(level, plans = []) {
  if (!level) return null;
  const direct = level.plan_url || level.template_url || level.floor_plan_url || null;
  if (direct && String(direct).trim()) return String(direct).trim();
  const planId = level.floor_plan_id || level.plan_id;
  if (!planId) return null;
  const plan = plans.find((p) => String(p.id) === String(planId));
  const url = plan?.plan_url;
  return url && String(url).trim() ? String(url).trim() : null;
}

function DrawingBadge({ uploaded }) {
  if (uploaded) {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white text-sm font-bold leading-none"
        title="Drawing uploaded"
      >
        V
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-300 text-gray-400 text-xs font-semibold"
      title="No drawing yet"
    >
      O
    </span>
  );
}

function blockCodeForIndex(index, total) {
  if (total === 1) return 'MAIN';
  if (total <= 26) return String.fromCharCode(64 + index);
  return `B${String(index).padStart(2, '0')}`;
}

/** Display level cell: -2, -1, 1, 2, Roof */
function levelDisplay(level) {
  const code = String(level?.code || '').toUpperCase();
  if (code === 'ROOF' || code.endsWith('_ROOF')) return 'Roof';
  const bm = code.match(/BM(\d+)$/);
  if (bm) return `-${bm[1]}`;
  const sort = Number(level?.sort_order);
  if (Number.isFinite(sort) && sort < 0) return String(sort);
  if (Number.isFinite(sort) && sort > 0 && sort < 900) return String(sort);
  const m = code.match(/L(\d+)$/);
  if (m) return m[1];
  return level?.label || code || '—';
}

function emptyBuildingRow(name = '') {
  return {
    name: name || '',
    fromFloor: 1,
    toFloor: 1,
    includeRoof: true,
  };
}

/**
 * Building structure & drawings — simplified:
 * 1) Building count → name + floor range (basement allowed) + roof
 * 2) Submit → flat Place list with name / position / drawing actions
 */
export default function FloorStructureSetup({
  buildingId,
  buildingName = '',
  buildingFloorPlanUrl = null,
  onComplete,
  embed = false,
  workerCanMoveUnitMarkers = false,
  onWorkerCanMoveUnitMarkersChange,
  isDefaultForWorkers = false,
  onIsDefaultForWorkersChange,
}) {
  const queryClient = useQueryClient();
  const [buildingCount, setBuildingCount] = useState(1);
  const [buildingRows, setBuildingRows] = useState(() => [emptyBuildingRow(buildingName)]);
  const [creating, setCreating] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [sketchTarget, setSketchTarget] = useState(null);
  const [savingLevelId, setSavingLevelId] = useState(null);

  const { data: levelsSetup, isLoading } = useQuery({
    queryKey: ['building', buildingId, 'levels-setup'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/levels-setup`)).data?.data,
    enabled: !!buildingId,
  });

  const { data: status } = useQuery({
    queryKey: ['building', buildingId, 'floor-setup-status'],
    queryFn: async () =>
      (await api.get(`/buildings/${buildingId}/levels-setup/status`)).data?.data,
    enabled: !!buildingId,
  });

  const blocks = useMemo(
    () => (Array.isArray(levelsSetup?.blocks) ? levelsSetup.blocks : []),
    [levelsSetup]
  );
  const levels = useMemo(
    () => (Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : []),
    [levelsSetup]
  );
  const plans = useMemo(
    () => (Array.isArray(levelsSetup?.plans) ? levelsSetup.plans : []),
    [levelsSetup]
  );

  // Keep building-count form length in sync with count
  useEffect(() => {
    const n = Math.max(1, Math.min(30, parseInt(buildingCount, 10) || 1));
    setBuildingRows((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        const next = [...prev];
        while (next.length < n) {
          next.push(emptyBuildingRow(n === 1 ? buildingName : ''));
        }
        return next;
      }
      return prev.slice(0, n);
    });
  }, [buildingCount, buildingName]);

  // Hydrate form from saved blocks once when structure first loads
  const hydratedRef = useRef(false);
  useEffect(() => {
    hydratedRef.current = false;
  }, [buildingId]);
  useEffect(() => {
    if (!levelsSetup || !blocks.length || hydratedRef.current) return;
    hydratedRef.current = true;
    const rows = blocks.map((b) => {
      const bl = levels.filter((l) => String(l.block_id) === String(b.id));
      const nums = bl
        .map((l) => Number(l.sort_order))
        .filter((n) => Number.isFinite(n) && Math.abs(n) < 900);
      const hasRoof = bl.some((l) => String(l.code || '').toUpperCase().includes('ROOF'));
      const fromFloor = nums.length ? Math.min(...nums) : 1;
      const positive = nums.filter((n) => n > 0);
      const toFloor = positive.length
        ? Math.max(...positive)
        : nums.length
          ? Math.max(...nums)
          : 1;
      return {
        name:
          (b.building_group_label && String(b.building_group_label).trim()) ||
          b.label ||
          buildingName ||
          '',
        fromFloor: fromFloor === 0 ? 1 : fromFloor,
        toFloor: toFloor === 0 ? 1 : toFloor,
        includeRoof: hasRoof,
      };
    });
    setBuildingCount(rows.length || 1);
    setBuildingRows(rows.length ? rows : [emptyBuildingRow(buildingName)]);
  }, [levelsSetup, blocks, levels, buildingName]);

  const placeRows = useMemo(() => {
    const defaultBuild = buildingName || 'Building';
    // Per-Place drawings only — never fall back to buildings.floor_plan_url
    // (that made every empty floor show the same sketch).
    return (levels || []).map((l) => {
      const block = blocks.find((b) => String(b.id) === String(l.block_id));
      const buildName =
        (block?.building_group_label && String(block.building_group_label).trim()) ||
        (block && String(block.code).toUpperCase() === 'MAIN' ? block.label : null) ||
        block?.label ||
        defaultBuild;
      const planUrl = resolveLevelPlanUrl(l, plans) || null;
      return {
        level: l,
        buildName,
        levelLabel: levelDisplay(l),
        placeName: l.label || '',
        position: l.position_code || '',
        planUrl,
        hasDrawing: !!planUrl,
      };
    });
  }, [levels, blocks, plans, buildingName]);

  const invalidate = () => {
    queryClient.invalidateQueries(['building', buildingId, 'levels-setup']);
    queryClient.invalidateQueries(['building', buildingId, 'floor-setup-status']);
    queryClient.invalidateQueries(['building', buildingId]);
  };

  const updateBuildingRow = (idx, patch) => {
    setBuildingRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const buildRangesClient = (fromFloor, toFloor, includeRoof) => {
    let from = parseInt(fromFloor, 10);
    let to = parseInt(toFloor, 10);
    if (!Number.isFinite(from)) from = 1;
    if (!Number.isFinite(to)) to = from;
    if (to < from) [from, to] = [to, from];
    const ranges = [];
    for (let n = from; n <= to; n++) {
      if (n === 0) continue;
      const code = n < 0 ? `BM${Math.abs(n)}` : `L${n}`;
      const label =
        n < 0
          ? `Basement ${Math.abs(n)} (B${Math.abs(n)})`
          : n === 1
            ? 'Ground / Level 1'
            : `Level ${n}`;
      ranges.push({ code, label, planCode: code, planLabel: label, sortOrder: n });
    }
    if (includeRoof) {
      ranges.push({
        code: 'ROOF',
        label: 'Roof',
        planCode: 'ROOF',
        planLabel: 'Roof',
        sortOrder: 1000,
      });
    }
    return ranges;
  };

  const submitStructure = async () => {
    const n = Math.max(1, Math.min(30, parseInt(buildingCount, 10) || 1));
    const rows = buildingRows.slice(0, n);
    for (let i = 0; i < rows.length; i++) {
      if (!String(rows[i].name || '').trim()) {
        toast.error(`Enter a name for building ${i + 1}`);
        return;
      }
    }
    if (levels.length) {
      const ok = window.confirm(
        'Rebuild places from this list?\n\nExisting place rows are replaced. Drawings on matching plan codes may be kept when codes match.'
      );
      if (!ok) return;
    }
    setCreating(true);
    try {
      const blocksPayload = rows.map((r, i) => {
        const name = String(r.name).trim();
        const code = blockCodeForIndex(i + 1, rows.length);
        return {
          code,
          label: rows.length === 1 ? 'Main' : name,
          building_group_label: name,
          level_mode: 'EACH',
          sort_order: i + 1,
          ranges: buildRangesClient(r.fromFloor, r.toFloor, r.includeRoof !== false),
        };
      });
      await api.post(`/buildings/${buildingId}/levels-setup/configure-site`, {
        replace: true,
        blocks: blocksPayload,
      });
      toast.success(levels.length ? 'Places rebuilt' : 'Places created');
      invalidate();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Could not create places');
    } finally {
      setCreating(false);
    }
  };

  const savePlaceFields = async (levelId, { placeName, position }) => {
    setSavingLevelId(levelId);
    try {
      await api.put(`/buildings/${buildingId}/levels-setup/levels/${levelId}`, {
        label: placeName,
        place_name: placeName,
        position_code: position,
        position,
      });
      invalidate();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save place');
    } finally {
      setSavingLevelId(null);
    }
  };

  const uploadLevelPlan = async (level, file) => {
    if (!file) return;
    setUploadingId(level.id);
    try {
      const planId = level.floor_plan_id || level.plan_id;
      if (!planId) {
        toast.error('No plan template for this place — create places first');
        return;
      }
      const uploadType = `floor-plans/building_${buildingId}/level_${level.code || level.id}`;
      const fd = new FormData();
      fd.append('type', uploadType);
      fd.append('file', file);
      const uploadRes = await api.post(
        `/files/upload?type=${encodeURIComponent(uploadType)}`,
        fd,
        {
          headers: { 'X-Upload-Type': uploadType },
          timeout: 0,
          skipErrorToast: true,
        }
      );
      const planUrl = planUrlFromUploadResponse(uploadRes.data);
      if (!planUrl) {
        toast.error('Upload succeeded but no file URL returned');
        return;
      }
      await api.put(`/buildings/${buildingId}/levels-setup/plans/${planId}`, {
        plan_url: planUrl,
      });
      toast.success(`${level.label || 'Place'} drawing saved`);
      invalidate();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const clearDrawing = async (level) => {
    const planId = level.floor_plan_id || level.plan_id;
    if (!planId) {
      toast.error('No drawing sheet linked to this place');
      return;
    }
    const ok = window.confirm(`Remove drawing for "${level.label || level.code}"?`);
    if (!ok) return;
    try {
      // Empty string (not null) so JSON always sends plan_url and the API clears it.
      await api.put(`/buildings/${buildingId}/levels-setup/plans/${planId}`, {
        plan_url: '',
      });
      toast.success('Drawing removed');
      invalidate();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not remove drawing');
    }
  };

  const completeMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/buildings/${buildingId}/levels-setup/status`, {
          floor_setup_complete: true,
          floor_setup_draft: false,
        })
      ).data?.data,
    onSuccess: () => {
      toast.success('Building structure saved — opening Floor plan');
      invalidate();
      onComplete?.();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const draftMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/buildings/${buildingId}/levels-setup/status`, {
          floor_setup_complete: false,
          floor_setup_draft: true,
        })
      ).data?.data,
    onSuccess: () => {
      toast.success('Saved as draft');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Draft save failed'),
  });

  if (!buildingId) {
    return (
      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        Save the building first, then set floors and drawings here.
      </p>
    );
  }

  return (
    <>
      <div
        id="floor-structure"
        className={`bg-white rounded-xl border-2 border-slate-300 overflow-hidden shadow-sm ${embed ? '' : ''}`}
      >
        <div className="px-5 py-4 border-b-2 border-slate-200 bg-slate-50">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="text-base font-semibold text-gray-900">Building structure &amp; drawings</h2>
            <InfoButton title="Building structure & drawings">
              <p>
                Enter how many buildings are on this site, then each building’s name and floor range. Floors may
                start below ground (−1, −2…). Roof is added by default (+1). After Create places, set{' '}
                <strong>Name of place</strong> and <strong>position</strong>, then upload / sketch drawings.
              </p>
            </InfoButton>
            {status?.floor_setup_draft !== false ? (
              <span className="text-xs text-amber-700 font-medium">Draft</span>
            ) : null}
            {status?.floor_setup_complete ? (
              <span className="text-xs text-emerald-700 font-medium">Setup complete</span>
            ) : null}
          </div>
        </div>

        <div className="p-4 space-y-4 border-b">
          <label className="block text-sm max-w-xs">
            <span className="font-medium text-gray-800">Number of buildings</span>
            <input
              type="number"
              min={1}
              max={30}
              value={buildingCount}
              onChange={(e) => setBuildingCount(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </label>

          <div className="space-y-3">
            {buildingRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <label className="sm:col-span-4 text-sm">
                  <span className="text-xs font-medium text-gray-600">Building name</span>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateBuildingRow(idx, { name: e.target.value })}
                    placeholder={buildingName || `Building ${idx + 1}`}
                    className="mt-1 w-full border rounded-lg px-2.5 py-1.5 bg-white"
                  />
                </label>
                <label className="sm:col-span-2 text-sm">
                  <span className="text-xs font-medium text-gray-600 inline-flex items-center gap-1">
                    From floor
                    <InfoButton title="From floor">
                      <p>
                        Lowest floor. Use negatives for basement: <strong>−1</strong> = B1,{' '}
                        <strong>−2</strong> = B2. There is no floor 0.
                      </p>
                    </InfoButton>
                  </span>
                  <input
                    type="number"
                    min={-30}
                    max={200}
                    value={row.fromFloor}
                    onChange={(e) => updateBuildingRow(idx, { fromFloor: e.target.value })}
                    className="mt-1 w-full border rounded-lg px-2.5 py-1.5 bg-white"
                  />
                </label>
                <label className="sm:col-span-2 text-sm">
                  <span className="text-xs font-medium text-gray-600">To floor</span>
                  <input
                    type="number"
                    min={-30}
                    max={200}
                    value={row.toFloor}
                    onChange={(e) => updateBuildingRow(idx, { toFloor: e.target.value })}
                    className="mt-1 w-full border rounded-lg px-2.5 py-1.5 bg-white"
                  />
                </label>
                <label className="sm:col-span-2 flex items-center gap-2 text-sm pb-2">
                  <input
                    type="checkbox"
                    checked={row.includeRoof !== false}
                    onChange={(e) => updateBuildingRow(idx, { includeRoof: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    Roof <span className="text-gray-400">(+1)</span>
                  </span>
                </label>
                <div className="sm:col-span-2 text-[11px] text-gray-500 pb-2">
                  Building {idx + 1}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={creating || isLoading}
              onClick={submitStructure}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              <Plus className="w-4 h-4" />
              {creating ? 'Creating…' : levels.length ? 'Rebuild places' : 'Create places'}
            </button>
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Places &amp; drawings</h3>
          {!placeRows.length ? (
            <p className="text-sm text-gray-500">
              No places yet — set building count and floors above, then Create places.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Build Name</th>
                    <th className="text-left px-3 py-2 font-semibold">Level</th>
                    <th className="text-left px-3 py-2 font-semibold">Name of place</th>
                    <th className="text-left px-3 py-2 font-semibold">position</th>
                    <th className="text-center px-3 py-2 font-semibold">Drawing</th>
                    <th className="text-left px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {placeRows.map((row) => (
                    <PlaceRow
                      key={row.level.id}
                      row={row}
                      uploading={uploadingId === row.level.id}
                      saving={savingLevelId === row.level.id}
                      onSaveFields={savePlaceFields}
                      onUpload={(f) => uploadLevelPlan(row.level, f)}
                      onSketch={() => {
                        const planUrl =
                          row.planUrl ||
                          resolveLevelPlanUrl(row.level, plans) ||
                          row.level?.plan_url ||
                          null;
                        setSketchTarget({
                          level: row.level,
                          planUrl: planUrl ? String(planUrl).trim() : null,
                        });
                      }}
                      onClearDrawing={() => clearDrawing(row.level)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t bg-white space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!workerCanMoveUnitMarkers}
              onChange={(e) => onWorkerCanMoveUnitMarkersChange?.(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-x-2">
                <span className="text-sm font-medium text-gray-900">
                  Allow workers to move unit markers on the floor plan
                </span>
                <WorkerMarkersInfoButton className="text-gray-600" />
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!isDefaultForWorkers}
              onChange={(e) => onIsDefaultForWorkersChange?.(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-x-2">
                <span className="text-sm font-medium text-gray-900">
                  Default building for field workers (app download)
                </span>
                <DefaultBuildingInfoButton className="text-gray-600" />
              </span>
            </span>
          </label>
        </div>

        <div className="px-4 py-3 border-t bg-slate-50 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            disabled={!placeRows.length || draftMutation.isPending}
            onClick={() => draftMutation.mutate()}
            className="px-4 py-2 text-sm border rounded-lg bg-white disabled:opacity-40"
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={!placeRows.length || completeMutation.isPending}
            onClick={() => completeMutation.mutate()}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-40"
          >
            {completeMutation.isPending ? 'Saving…' : 'Save & open Floor plan'}
          </button>
        </div>
      </div>

      {sketchTarget?.level ? (
        <BuildingSketchModal
          key={`sketch-${sketchTarget.level.id}-${sketchTarget.planUrl || 'new'}`}
          title={
            sketchTarget.planUrl
              ? `Edit drawing — ${sketchTarget.level.label || sketchTarget.level.code}`
              : `Sketch — ${sketchTarget.level.label || sketchTarget.level.code}`
          }
          backgroundUrl={sketchTarget.planUrl || null}
          onClose={() => setSketchTarget(null)}
          onConfirm={async (file) => {
            const { level } = sketchTarget;
            await uploadLevelPlan(level, file);
            setSketchTarget(null);
          }}
        />
      ) : null}
    </>
  );
}

function PlaceRow({
  row,
  uploading,
  saving,
  onSaveFields,
  onUpload,
  onSketch,
  onClearDrawing,
}) {
  const [placeName, setPlaceName] = useState(row.placeName);
  const [position, setPosition] = useState(row.position);

  useEffect(() => {
    setPlaceName(row.placeName);
    setPosition(row.position);
  }, [row.placeName, row.position, row.level.id]);

  const commit = () => {
    if (placeName === row.placeName && position === row.position) return;
    onSaveFields(row.level.id, { placeName, position });
  };

  return (
    <tr>
      <td className="px-3 py-2 whitespace-nowrap text-gray-900 font-medium">{row.buildName}</td>
      <td className="px-3 py-2 whitespace-nowrap font-mono text-gray-800">{row.levelLabel}</td>
      <td className="px-3 py-2 min-w-[10rem]">
        <input
          type="text"
          value={placeName}
          onChange={(e) => setPlaceName(e.target.value)}
          onBlur={commit}
          disabled={saving}
          placeholder="Name of place"
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2 min-w-[7rem]">
        <input
          type="text"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          onBlur={commit}
          disabled={saving}
          placeholder="e.g. A"
          className="w-full border rounded px-2 py-1 text-sm font-mono uppercase"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <div className="inline-flex items-center gap-2">
          <DrawingBadge uploaded={row.hasDrawing} />
          {row.hasDrawing ? (
            <div className="w-12 h-8 rounded border overflow-hidden shrink-0">
              <AuthImage filename={row.planUrl} alt="" className="w-full h-full object-cover" />
            </div>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={uploading}
            onClick={onSketch}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-semibold disabled:opacity-50"
          >
            <Pencil className="w-3 h-3" />
            {row.hasDrawing ? 'Edit' : 'Sketch'}
          </button>
          <FloorPlanUploadButton
            disabled={uploading}
            uploading={uploading}
            label={row.hasDrawing ? 'Replace' : 'Upload'}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-semibold disabled:opacity-50"
            onFileReady={onUpload}
          />
          {row.hasDrawing ? (
            <button
              type="button"
              onClick={onClearDrawing}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 text-xs font-semibold"
            >
              <Trash2 className="w-3 h-3" />
              Delete
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
